import { BedDouble, Bath, Users, ArrowUpRight, Star } from "lucide-react";
import { FadeUp } from "./motion";

export default function PropertyCard({ p, i = 0, compact = false, suffix = "" }) {
  return (
    <FadeUp delay={i * 0.08} className="h-full">
      <article
        data-testid={`property-card-${p.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-forest/12 bg-cream transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_70px_rgba(15,38,28,0.14)]"
      >
        <div className="relative overflow-hidden">
          <img
            src={p.photo}
            alt={p.title}
            loading="lazy"
            className={`${compact ? "h-52" : "h-72"} w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105`}
          />
          {p.price != null && (
            <span
              data-testid={`property-price-${p.id}`}
              className="absolute bottom-4 left-4 rounded-full bg-forest/90 px-4 py-1.5 font-mono text-xs font-semibold text-cream backdrop-blur"
            >
              from €{p.price} / night
            </span>
          )}
          <span className="absolute right-4 top-4 rounded-full border border-white/20 bg-forest/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-cream/90 backdrop-blur">
            {p.type}
          </span>
        </div>
        <div className={`flex flex-1 flex-col ${compact ? "p-6" : "p-8"}`}>
          <p className="eyebrow text-forest-700/70">{p.area}</p>
          <h3 className={`mt-2.5 font-serif font-medium text-forest ${compact ? "text-xl" : "text-2xl"}`}>{p.name}</h3>
          {p.rating != null && (
            <a
              href={p.airbnb_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`property-rating-${p.id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-charcoal/70 transition-colors duration-300 hover:text-forest-700"
            >
              <Star className="h-3.5 w-3.5 fill-gold text-gold" />
              {p.rating} · {p.rating_count} reviews on Airbnb
            </a>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-charcoal/60" data-testid={`property-specs-${p.id}`}>
            {p.beds != null && (
              <span className="flex items-center gap-1.5">
                <BedDouble className="h-4 w-4 text-forest-700" />
                {p.beds} bed{p.beds > 1 ? "s" : ""}
              </span>
            )}
            {p.baths != null && (
              <span className="flex items-center gap-1.5">
                <Bath className="h-4 w-4 text-forest-700" />
                {p.baths} bath{p.baths > 1 ? "s" : ""}
              </span>
            )}
            {p.sleeps != null && (
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-forest-700" />
                Sleeps {p.sleeps}
              </span>
            )}
          </div>
          {!compact && (
            <>
              {p.blurb ? <p className="mt-4 text-sm leading-relaxed text-charcoal/65">{p.blurb}</p> : null}
              {p.tags?.length ? (
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span key={t} className="rounded-full border border-forest/12 bg-bone px-3 py-1.5 text-[11px] font-medium text-charcoal/60">
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
              {p.reviews?.length ? (
                <div className="mt-6 border-t border-forest/10 pt-5" data-testid={`property-reviews-${p.id}`}>
                  {p.reviews.slice(0, 2).map((r, ri) => (
                    <div key={ri} className={ri ? "mt-4" : ""} data-testid={`property-review-${p.id}-${ri}`}>
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, si) => (
                          <Star key={si} className="h-3.5 w-3.5 fill-gold text-gold" />
                        ))}
                      </div>
                      <p className="mt-2 font-serif text-sm italic leading-relaxed text-charcoal/75">"{r.quote}"</p>
                      <p className="mt-1.5 text-[11px] font-medium text-charcoal/45">
                        {r.guest}{r.date ? ` · ${r.date}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
          <a
            href={`${p.url}${suffix}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`property-book-${p.id}`}
            className={`mt-auto ${compact ? "pt-5" : "pt-7"}`}
          >
            <span className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-forest-700">
              {compact ? "Book Direct" : "Check Availability & Book Direct"}
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </a>
        </div>
      </article>
    </FadeUp>
  );
}
